/*
 * Orlento redesign — behaviour layer.
 * Adds accessibility and new-component behaviour AROUND the Umino theme's
 * existing JS (theme.min.js, product.min.js, collection.min.js, cart.js).
 * It never replaces the theme's cart, search, variant or filter logic:
 * it toggles the same classes/elements those scripts own.
 *
 * Modules register with Orlento.register(name, init). init(root) must be
 * idempotent: it marks what it touched with data-orlento-init and skips it
 * on the next call. Orlento.init(root) runs on DOMContentLoaded and on every
 * Shopify Theme Editor section load.
 */
(function () {
  'use strict';

  if (window.Orlento && window.Orlento.__loaded) return;

  var modules = [];
  var toastTimer = null;

  var Orlento = {
    __loaded: true,
    routes: (window.routes || {}),

    register: function (name, init) {
      modules.push({ name: name, init: init });
    },

    init: function (root) {
      var scope = root || document;
      modules.forEach(function (m) {
        try {
          m.init(scope);
        } catch (error) {
          if (window.console) console.error('[orlento:' + m.name + ']', error);
        }
      });
    },

    /* Mark an element as initialised by a module; returns false if already done. */
    claim: function (el, name) {
      var key = 'orlentoInit' + name;
      if (!el || el.dataset[key]) return false;
      el.dataset[key] = '1';
      return true;
    },

    toast: function (text) {
      var el = document.getElementById('orlento-toast');
      if (!el) {
        el = document.createElement('div');
        el.id = 'orlento-toast';
        el.className = 'orlento-toast';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        document.body.appendChild(el);
      }
      el.textContent = text;
      el.classList.add('show');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { el.classList.remove('show'); }, 1800);
    },

    formatMoney: function (cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.cartStrings) {
        return Shopify.formatMoney(cents, cartStrings.money_format);
      }
      return (cents / 100).toFixed(2);
    },

    prefersReducedMotion: function () {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    /* Keep Tab inside a dialog/drawer while it is open. Returns a release fn. */
    trapFocus: function (container) {
      var selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      function onKey(event) {
        if (event.key !== 'Tab') return;
        var items = Array.prototype.filter.call(container.querySelectorAll(selector), function (el) {
          return el.offsetParent !== null;
        });
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
      container.addEventListener('keydown', onKey);
      return function () { container.removeEventListener('keydown', onKey); };
    },

    /* Add a variant to the cart through the theme's own endpoint, then open
       the theme's cart drawer (cart-notification) so counts/totals stay in sync. */
    addToCart: function (variantId, quantity) {
      var routes = window.routes || {};
      var body = JSON.stringify({ id: variantId, quantity: quantity || 1, sections: 'form-mini-cart', sections_url: window.location.pathname });
      return fetch(routes.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: body
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      }).then(function (data) {
        Orlento.syncCartFromSections(data);
        return data;
      });
    },

    /* Mirror what product.min.js does after an add: swap the drawer form,
       refresh the header counts, open the drawer. */
    syncCartFromSections: function (data) {
      var form = document.getElementById('form-mini-cart');
      if (form && data && data.sections && data.sections['form-mini-cart']) {
        var parsed = new DOMParser().parseFromString(data.sections['form-mini-cart'], 'text/html');
        var fresh = parsed.querySelector('#form-mini-cart');
        if (fresh) form.innerHTML = fresh.innerHTML;
      }
      return fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.json', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.querySelectorAll('.cart-count').forEach(function (el) {
            el.textContent = el.classList.contains('cart-count-drawer') ? '(' + cart.item_count + ')' : cart.item_count;
          });
          var totals = document.querySelector('header-total-price');
          if (totals && typeof totals.updateTotal === 'function') totals.updateTotal(cart);
          document.querySelectorAll('free-ship-progress-bar').forEach(function (bar) {
            if (typeof bar.init === 'function') bar.init(cart.items_subtotal_price);
          });
          var drawer = document.querySelector('cart-notification');
          if (drawer) {
            if (typeof drawer.cartAction === 'function') drawer.cartAction();
            if (typeof drawer.open === 'function') drawer.open();
          }
          return cart;
        });
    }
  };

  window.Orlento = Orlento;

  /* ------------------------------------------------------------------------
   * Module: global ESC / aria-expanded for the theme's own drawers
   * (mobile nav, search popup, cart drawer, collection filter drawer).
   * Closing is done by clicking the theme's own close controls so theme.min.js
   * keeps full ownership of state.
   * ---------------------------------------------------------------------- */
  Orlento.register('drawers', function (root) {
    if (!Orlento.claim(document.documentElement, 'Drawers')) return;

    var html = document.documentElement;
    var lastTrigger = null;

    function click(selector) {
      var el = document.querySelector(selector);
      if (el) { el.click(); return true; }
      return false;
    }

    function closeAnyOpen() {
      var closed = false;
      if (html.classList.contains('nav-open')) {
        closed = click('.bls_main_menu .close-menu') || closed;
      }
      if (html.classList.contains('open-search')) {
        closed = click('.mini_search_header .button-close') || closed;
      }
      var cart = document.getElementById('bls-header_minicart');
      if (cart && cart.classList.contains('bls__opend-popup-header')) {
        closed = click('.bls-minicart-wrapper .close-button') || closed;
      }
      var filters = document.getElementById('CollectionSidebar');
      if (filters && filters.classList.contains('bls__opend-popup')) {
        closed = click('#sidebar__close') || closed;
      }
      if (closed && lastTrigger && typeof lastTrigger.focus === 'function') {
        lastTrigger.focus();
      }
      return closed;
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeAnyOpen();
    });

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('.nav-toggle, .top-search-toggle, .bls-minicart-action, .btn-filter');
      if (trigger) lastTrigger = trigger;
    }, true);

    /* Reflect open state on the triggers for assistive tech. */
    var observer = new MutationObserver(function () {
      var navOpen = html.classList.contains('nav-open');
      document.querySelectorAll('.nav-toggle').forEach(function (el) {
        el.setAttribute('aria-expanded', navOpen ? 'true' : 'false');
      });
      var searchOpen = html.classList.contains('open-search');
      document.querySelectorAll('.top-search-toggle').forEach(function (el) {
        el.setAttribute('aria-expanded', searchOpen ? 'true' : 'false');
      });
      var nav = document.querySelector('.bls__canvas-mb-menu');
      if (nav) nav.setAttribute('aria-hidden', navOpen ? 'false' : 'true');
      if (navOpen) {
        var firstLink = nav && nav.querySelector('a, button');
        if (firstLink && !nav.contains(document.activeElement)) firstLink.focus();
      }
    });
    observer.observe(html, { attributes: true, attributeFilter: ['class'] });

    var cartHost = document.getElementById('bls-header_minicart');
    if (cartHost) {
      new MutationObserver(function () {
        var open = cartHost.classList.contains('bls__opend-popup-header');
        document.querySelectorAll('.bls-minicart-action').forEach(function (el) {
          el.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
        var wrapper = cartHost.querySelector('.bls-minicart-wrapper');
        if (wrapper) {
          wrapper.setAttribute('aria-hidden', open ? 'false' : 'true');
          if (open) {
            var closeBtn = wrapper.querySelector('.close-button');
            if (closeBtn) closeBtn.focus();
          }
        }
      }).observe(cartHost, { attributes: true, attributeFilter: ['class'] });
    }
  });

  /* ------------------------------------------------------------------------
   * Module: Buy now (cards + quick view). Adds the chosen variant through the
   * cart API, then goes to checkout. Multi-variant cards open Quick View
   * instead so a customer never gets an arbitrary variant.
   * ---------------------------------------------------------------------- */
  Orlento.register('buyNow', function () {
    if (!Orlento.claim(document.documentElement, 'BuyNow')) return;

    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-orlento-buy-now]');
      if (!button) return;
      event.preventDefault();

      var card = button.closest('.orlento-product-card, .bls__product-item');
      var form = button.closest('form');
      var variantId = null;
      var quantity = 1;

      if (form) {
        var idInput = form.querySelector('input[name="id"], select[name="id"]');
        var qtyInput = form.querySelector('input[name="quantity"]');
        variantId = idInput && idInput.value;
        quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      } else if (card) {
        if (card.dataset.orlentoVariants && parseInt(card.dataset.orlentoVariants, 10) > 1) {
          var qv = card.querySelector('.bls__product-quickview');
          if (qv) qv.click();
          return;
        }
        var quickBuy = card.querySelector('.bls__product-addtocart-js');
        variantId = (quickBuy && quickBuy.dataset.productVariantId) || card.dataset.orlentoVariantId;
      }

      if (!variantId) return;

      button.classList.add('btn-loading');
      button.setAttribute('aria-busy', 'true');
      fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          window.location.href = '/checkout';
        });
      }).catch(function (error) {
        button.classList.remove('btn-loading');
        button.removeAttribute('aria-busy');
        var message = (error && (error.description || error.message)) || (window.cartStrings && cartStrings.error) || 'Could not start checkout.';
        Orlento.toast(message);
      });
    });
  });


  /* ------------------------------------------------------------------------
   * Module: FAQ accordion (sections/orlento-faq.liquid)
   * Delegated click on the question buttons toggles the item; when the
   * section does not allow several open answers the siblings close. Theme
   * Editor block selection opens the selected item.
   * ---------------------------------------------------------------------- */
  Orlento.register('faq', function (root) {
    var scope = root || document;
    var sections = [];
    if (scope.matches && scope.matches('.orlento-faq')) sections.push(scope);
    if (scope.querySelectorAll) {
      Array.prototype.push.apply(sections, scope.querySelectorAll('.orlento-faq'));
    }

    function setOpen(item, open) {
      if (!item) return;
      item.classList.toggle('is-open', open);
      var button = item.querySelector('.orlento-faq__q');
      if (button) button.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function openItem(item, section) {
      if (!item || !section) return;
      if (section.dataset.allowMultiple !== 'true') {
        Array.prototype.forEach.call(section.querySelectorAll('.orlento-faq__item.is-open'), function (other) {
          if (other !== item) setOpen(other, false);
        });
      }
      setOpen(item, true);
    }

    sections.forEach(function (section) {
      if (!Orlento.claim(section, 'Faq')) return;

      section.addEventListener('click', function (event) {
        var button = event.target.closest('.orlento-faq__q');
        if (!button || !section.contains(button)) return;
        var item = button.closest('.orlento-faq__item');
        if (!item) return;
        if (item.classList.contains('is-open')) {
          setOpen(item, false);
        } else {
          openItem(item, section);
        }
      });
    });

    /* Theme Editor: open the block the merchant selects (listener bound once). */
    if (Orlento.claim(document.documentElement, 'FaqBlockSelect')) {
      document.addEventListener('shopify:block:select', function (event) {
        var item = event.target && event.target.closest && event.target.closest('.orlento-faq__item');
        if (!item) return;
        openItem(item, item.closest('.orlento-faq'));
      });
    }
  });


  /* ------------------------------------------------------------------------
     WhatsApp reviews slider (sections/orlento-whatsapp-reviews.liquid)
     Desktop (≥760px): arrows translate the track by whole cards; 3 cards are
     visible below 1100px, 5 above. Mobile: native scroll-snap, JS does nothing
     except scroll the selected block into view in the Theme Editor.
     ------------------------------------------------------------------------ */
  Orlento.register('reviews', function (root) {
    var scope = root || document;
    var list = [];
    if (scope.nodeType === 1 && scope.matches && scope.matches('.orlento-reviews')) list.push(scope);
    Array.prototype.forEach.call(scope.querySelectorAll ? scope.querySelectorAll('.orlento-reviews') : [], function (el) {
      if (list.indexOf(el) === -1) list.push(el);
    });

    var desktopQuery = window.matchMedia ? window.matchMedia('(min-width: 760px)') : null;
    var wideQuery = window.matchMedia ? window.matchMedia('(min-width: 1100px)') : null;

    function isDesktop() { return desktopQuery ? desktopQuery.matches : window.innerWidth >= 760; }
    function visibleCount() { return (wideQuery ? wideQuery.matches : window.innerWidth >= 1100) ? 5 : 3; }

    list.forEach(function (el) {
      if (!Orlento.claim(el, 'Reviews')) return;

      var viewport = el.querySelector('.orlento-reviews__viewport');
      var track = el.querySelector('.orlento-reviews__track');
      var prev = el.querySelector('.orlento-reviews__arrow[data-dir="-1"]');
      var next = el.querySelector('.orlento-reviews__arrow[data-dir="1"]');
      if (!viewport || !track) return;

      var index = 0;
      var frame = null;

      function cards() { return Array.prototype.slice.call(track.querySelectorAll('.orlento-reviews__card')); }
      function maxIndex() { return Math.max(0, cards().length - visibleCount()); }
      function step() {
        var first = cards()[0];
        if (!first) return 0;
        var gap = parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap) || 0;
        return first.getBoundingClientRect().width + gap;
      }
      function setDisabled(btn, state) {
        if (!btn) return;
        btn.setAttribute('aria-disabled', state ? 'true' : 'false');
      }
      function update() {
        if (!isDesktop()) {
          index = 0;
          track.style.transform = '';
          return;
        }
        index = Math.min(Math.max(0, index), maxIndex());
        if (Orlento.prefersReducedMotion()) track.style.transition = 'none';
        track.style.transform = index ? 'translateX(-' + (index * step()) + 'px)' : '';
        setDisabled(prev, index <= 0);
        setDisabled(next, index >= maxIndex());
      }
      function go(delta) {
        var target = index + delta;
        if (target < 0 || target > maxIndex()) return;
        index = target;
        update();
      }
      function reveal(card) {
        var all = cards();
        var i = all.indexOf(card);
        if (i === -1) return;
        if (isDesktop()) {
          if (i < index) index = i;
          else if (i >= index + visibleCount()) index = i - visibleCount() + 1;
          update();
        } else {
          var behavior = Orlento.prefersReducedMotion() ? 'auto' : 'smooth';
          try {
            viewport.scrollTo({ left: card.offsetLeft - viewport.offsetLeft, behavior: behavior });
          } catch (error) {
            viewport.scrollLeft = card.offsetLeft - viewport.offsetLeft;
          }
        }
      }

      el.addEventListener('click', function (event) {
        var btn = event.target.closest('.orlento-reviews__arrow');
        if (!btn || !el.contains(btn)) return;
        event.preventDefault();
        if (btn.getAttribute('aria-disabled') === 'true') return;
        go(parseInt(btn.getAttribute('data-dir'), 10) || 0);
      });

      function onResize() {
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(function () {
          frame = null;
          update();
        });
      }
      window.addEventListener('resize', onResize);
      if (desktopQuery && desktopQuery.addEventListener) desktopQuery.addEventListener('change', onResize);
      else if (desktopQuery && desktopQuery.addListener) desktopQuery.addListener(onResize);

      el.orlentoReviewsReveal = reveal;
      update();
    });

    if (!document.documentElement.dataset.orlentoReviewsBlockSelect) {
      document.documentElement.dataset.orlentoReviewsBlockSelect = '1';
      document.addEventListener('shopify:block:select', function (event) {
        var card = event.target && event.target.closest ? event.target.closest('.orlento-reviews__card') : null;
        if (!card) return;
        var section = card.closest('.orlento-reviews');
        if (section && typeof section.orlentoReviewsReveal === 'function') section.orlentoReviewsReveal(card);
      });
    }
  });


  /* ------------------------------------------------------------------------
   * Module: homepage brand tabs (sections/collection-tab.liquid, Orlento chips)
   * The theme's BlsProductTabEvents switches panes; this keeps the view-all
   * link and aria state in sync and adds keyboard support for the chip row.
   * ---------------------------------------------------------------------- */
  Orlento.register('tabs', function (root) {
    var scope = root && root.querySelectorAll ? root : document;
    var sections = scope.querySelectorAll('.orlento-tabs');
    if (scope.matches && scope.matches('.orlento-tabs')) sections = [scope];

    Array.prototype.forEach.call(sections, function (section) {
      if (!Orlento.claim(section, 'Tabs')) return;
      var link = section.querySelector('[data-orlento-view-all]');
      var text = section.querySelector('[data-orlento-view-all-text]');

      function sync() {
        var active = section.querySelector('.bls__collection-tab-item.active');
        section.querySelectorAll('.bls__collection-tab-item').forEach(function (tab) {
          tab.setAttribute('aria-pressed', tab === active ? 'true' : 'false');
        });
        if (link && active && active.dataset.url) {
          link.href = active.dataset.url;
          if (text) text.textContent = (link.dataset.label || '') + ' ' + (active.dataset.title || '');
        }
        // Keep the active chip visible inside its own strip only; never move the page.
        var strip = active && active.parentElement;
        if (strip && strip.scrollWidth > strip.clientWidth + 1) {
          var target = Math.max(0, active.offsetLeft - strip.offsetLeft - 16);
          if (typeof strip.scrollTo === 'function') strip.scrollTo({ left: target, behavior: Orlento.prefersReducedMotion() ? 'auto' : 'smooth' });
          else strip.scrollLeft = target;
        }
      }

      section.addEventListener('click', function (event) {
        if (event.target.closest('.bls__collection-tab-item')) setTimeout(sync, 0);
      });

      section.addEventListener('keydown', function (event) {
        var tab = event.target.closest('.bls__collection-tab-item');
        if (!tab) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          tab.click();
        } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          var tabs = Array.prototype.slice.call(section.querySelectorAll('.bls__collection-tab-item'));
          var index = tabs.indexOf(tab);
          var next = tabs[event.key === 'ArrowRight' ? index + 1 : index - 1];
          if (next) { next.focus(); next.click(); }
        }
      });

      sync();
    });
  });


  /* ------------------------------------------------------------------------
   * Module: Video banner (sections/orlento-video-banner.liquid)
   * Plays only the <video> that is displayed for the current viewport,
   * honours prefers-reduced-motion (no autoplay, poster stays), drives the
   * play/pause pill, pauses off-screen and hides the video on load failure
   * so the poster image shows instead.
   * ---------------------------------------------------------------------- */
  Orlento.register('videoBanner', function (root) {
    var scope = root || document;
    var banners = [];
    if (scope.matches && scope.matches('.orlento-video-banner')) banners.push(scope);
    if (scope.querySelectorAll) {
      Array.prototype.push.apply(banners, scope.querySelectorAll('.orlento-video-banner'));
    }

    banners.forEach(function (banner) {
      if (!Orlento.claim(banner, 'VideoBanner')) return;

      var videos = Array.prototype.slice.call(banner.querySelectorAll('.orlento-video-banner__video'));
      if (!videos.length) return;

      var toggle = banner.querySelector('[data-orlento-video-toggle]');
      var toggleText = toggle && toggle.querySelector('.orlento-video-banner__toggle-text');
      var pauseLabel = banner.dataset.pauseLabel || 'Pause';
      var playLabel = banner.dataset.playLabel || 'Play';
      var autoplayWanted = banner.dataset.autoplay === 'true' && !Orlento.prefersReducedMotion();
      var userPaused = !autoplayWanted;
      var inView = true;
      var active = null;

      function isDisplayed(video) {
        return window.getComputedStyle(video).display !== 'none';
      }

      function pickActive() {
        var next = null;
        for (var i = 0; i < videos.length; i += 1) {
          if (isDisplayed(videos[i])) { next = videos[i]; break; }
        }
        return next;
      }

      function setPausedState(paused) {
        banner.classList.toggle('is-paused', paused);
        if (toggle) {
          toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
          if (toggleText) toggleText.textContent = paused ? playLabel : pauseLabel;
        }
      }

      function play(video) {
        if (!video) return;
        var promise = video.play();
        if (promise && typeof promise.then === 'function') {
          promise.then(function () {
            setPausedState(false);
          }).catch(function () {
            /* Autoplay blocked by the browser: show the Play state. */
            setPausedState(true);
          });
        } else {
          setPausedState(false);
        }
      }

      function pause(video) {
        if (!video) return;
        video.pause();
        setPausedState(true);
      }

      function sync() {
        var next = pickActive();
        active = next;
        /* Hidden videos (other viewport) must never play or keep buffering. */
        videos.forEach(function (video) {
          if (video !== active && !video.paused) video.pause();
        });
        if (!active) return;
        if (!userPaused && inView) {
          play(active);
        } else {
          pause(active);
        }
      }

      videos.forEach(function (video) {
        video.muted = true;
        if (!autoplayWanted) {
          video.removeAttribute('autoplay');
          video.pause();
        }
        if (!video.hasAttribute('controls')) video.setAttribute('tabindex', '-1');

        function fail() {
          banner.classList.add('is-video-failed');
          if (toggle) toggle.hidden = true;
        }
        video.addEventListener('error', fail);
        var sources = video.querySelectorAll('source');
        if (!sources.length) {
          fail();
        } else {
          /* The last <source> firing error means no source could be played. */
          sources[sources.length - 1].addEventListener('error', fail);
        }

        /* Keep the pill in sync with native controls / keyboard media keys. */
        video.addEventListener('play', function () {
          if (video === active) setPausedState(false);
        });
        video.addEventListener('pause', function () {
          if (video === active && (userPaused || !inView)) setPausedState(true);
        });
      });

      if (toggle) {
        toggle.addEventListener('click', function () {
          if (!active) active = pickActive();
          if (!active) return;
          if (active.paused) {
            userPaused = false;
            play(active);
          } else {
            userPaused = true;
            pause(active);
          }
        });
      }

      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            inView = entry.isIntersecting;
            if (!active) return;
            if (!inView) {
              if (!active.paused) active.pause();
            } else if (!userPaused) {
              play(active);
            }
          });
        }, { threshold: 0.15 });
        observer.observe(banner);
      }

      /* Swap desktop/mobile video when the viewport crosses the breakpoint. */
      if (videos.length > 1 && window.matchMedia) {
        var mql = window.matchMedia('(min-width: 760px)');
        var onChange = function () { sync(); };
        if (typeof mql.addEventListener === 'function') {
          mql.addEventListener('change', onChange);
        } else if (typeof mql.addListener === 'function') {
          mql.addListener(onChange);
        }
      }

      sync();
    });
  });


  /* ------------------------------------------------------------------------
   * Module: PDP sticky bar price sync. The theme updates the main price on
   * variant change; mirror it into the mobile sticky bar.
   * ---------------------------------------------------------------------- */
  Orlento.register('stickyPrice', function () {
    var target = document.querySelector('[data-orlento-sticky-price]');
    var source = document.querySelector('.orlento-pdp .orlento-pdp__price .price__regular .price');
    if (!target || !source || !Orlento.claim(target, 'StickyPrice')) return;
    var sync = function () { target.textContent = source.textContent.trim(); };
    new MutationObserver(sync).observe(source, { childList: true, characterData: true, subtree: true });
    sync();
  });


  /* ------------------------------------------------------------------------
   * Module: header hides on scroll down, returns on scroll up (stays sticky).
   * ---------------------------------------------------------------------- */
  Orlento.register('headerScroll', function () {
    var header = document.getElementById('page-header');
    if (!header || !Orlento.claim(header, 'HeaderScroll')) return;
    var lastY = window.scrollY;
    var ticking = false;
    var threshold = 12;

    function update() {
      ticking = false;
      var y = window.scrollY;
      var html = document.documentElement;
      var panelOpen = html.classList.contains('nav-open') || html.classList.contains('open-search') || html.classList.contains('hside_opened');
      if (panelOpen || y < header.offsetHeight) {
        header.classList.remove('orlento-header--hidden');
      } else if (y > lastY + threshold) {
        header.classList.add('orlento-header--hidden');
      } else if (y < lastY - threshold) {
        header.classList.remove('orlento-header--hidden');
      }
      lastY = y;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }, { passive: true });
  });


  /* ------------------------------------------------------------------------
   * Module: mobile drawer extras. The search button closes the menu (theme
   * close link) and opens the theme search popup.
   * ---------------------------------------------------------------------- */
  Orlento.register('drawerExtras', function () {
    if (!Orlento.claim(document.documentElement, 'DrawerExtras')) return;
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-orlento-open-search]');
      if (!trigger) return;
      event.preventDefault();
      var close = document.querySelector('.bls_main_menu .close-menu');
      if (close) close.click();
      window.setTimeout(function () {
        var toggle = document.querySelector('.top-search-toggle');
        if (toggle) toggle.click();
      }, 260);
    });
  });


/* Lift the WhatsApp chat widget (gty-whatsapp-chat-button app embed, open shadow
   root) above the sticky add-to-cart bar while the theme flags the bar as shown
   (body.sticky-addtocart-show). */
(function () {
  var sticky = document.getElementById('bls__sticky-addcart');
  if (!sticky) return;

  function host() { return document.querySelector('gty-whatsapp-chat-button'); }
  function wrapper() {
    var h = host();
    if (!h || !h.shadowRoot) return null;
    return h.shadowRoot.querySelector('.gty-wacb-wrapper');
  }

  function apply() {
    var w = wrapper();
    if (!w) return;
    var rect = sticky.getBoundingClientRect();
    var cs = getComputedStyle(sticky);
    var visible = rect.height > 0 && rect.top < window.innerHeight - 4 && rect.bottom > 0 && cs.visibility !== 'hidden' && cs.display !== 'none' && parseFloat(cs.opacity) > 0.5;
    if (visible) {
      var base = window.innerWidth >= 760 ? 24 : 16;
      var lift = Math.max(0, Math.round(window.innerHeight - rect.top) + 12 - base);
      w.style.transition = 'bottom 0.25s ease';
      host().style.setProperty('--orlento-wa-lift', lift + 'px');
    } else {
      host().style.removeProperty('--orlento-wa-lift');
    }
  }

  var observer = new MutationObserver(apply);
  observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  window.addEventListener('resize', apply);
  window.addEventListener('scroll', apply, { passive: true });

  var tries = 0;
  var timer = setInterval(function () {
    if (wrapper() || ++tries > 40) {
      clearInterval(timer);
      apply();
    }
  }, 250);
})();


/* Orlento motion system — vanilla, no libraries.
   Scroll reveals with stagger (IntersectionObserver), scrolled header state,
   hero parallax (rAF), magnetic primary buttons, click ripples, cart badge bump.
   Gated on html.orlento-motion so content is never hidden without JS. */
(function () {
  var doc = document.documentElement;
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (!('IntersectionObserver' in window)) return;
  doc.classList.add('orlento-motion');

  /* ---------- Scroll reveals ---------- */
  var REVEAL_GROUPS = [
    { sel: '.orlento-section-head, .bls__section-header, .orlento-faq__head, .orlento-reviews__head, .orlento-collection-header__text', type: 'up' },
    { sel: '.orlento-trust-row__item', type: 'up', stagger: 70 },
    { sel: '.orlento-collection-list__tile', type: 'scale', stagger: 60 },
    { sel: '.orlento-editorial__card', type: 'scale', stagger: 90 },
    { sel: '.orlento-reviews__card', type: 'up', stagger: 70 },
    { sel: '.orlento-faq__item', type: 'up', stagger: 50 },
    { sel: '.orlento-video-banner__copy', type: 'up' },
    { sel: '.orlento-pdp__info > *', type: 'up', stagger: 45 },
    { sel: '.orlento-cart-page__head', type: 'up' },
    { sel: 'footer.footer .bls__footer_block', type: 'up', stagger: 80 },
    { sel: '.orlento-product-card:not(.orlento-product-card--compact)', type: 'up', stagger: 55, grid: true }
  ];

  function markTargets(root) {
    REVEAL_GROUPS.forEach(function (g) {
      var nodes = (root || document).querySelectorAll(g.sel);
      Array.prototype.forEach.call(nodes, function (el) {
        if (el.hasAttribute('data-orlento-reveal')) return;
        if (el.closest('.swiper-slide, #bls__sticky-addcart, .dlg, .bls-minicart-wrapper, .bls_main_menu, [data-orlento-reveal]')) return;
        el.setAttribute('data-orlento-reveal', g.type);
        if (g.stagger) {
          var siblings = Array.prototype.filter.call(el.parentElement.children, function (s) {
            return s.hasAttribute('data-orlento-reveal') || s.matches(g.sel);
          });
          var i = siblings.indexOf(el);
          if (g.grid) i = i % 8;
          el.style.setProperty('--orlento-reveal-delay', Math.min(i * g.stagger, 480) + 'ms');
        }
        io.observe(el);
      });
    });
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      var el = e.target;
      el.classList.add('is-inview');
      io.unobserve(el);
      el.addEventListener('transitionend', function onEnd() {
        el.classList.add('is-settled');
        el.removeEventListener('transitionend', onEnd);
      });
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

  markTargets(document);

  // Anything already in view on load must not wait for a scroll
  requestAnimationFrame(function () {
    Array.prototype.forEach.call(document.querySelectorAll('[data-orlento-reveal]'), function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.92 && r.bottom > 0) el.classList.add('is-inview');
    });
  });

  // Content rendered later (filters, load more, section rerender in the editor)
  var mo = new MutationObserver(function (muts) {
    var dirty = false;
    muts.forEach(function (m) {
      if (m.addedNodes && m.addedNodes.length) dirty = true;
    });
    if (dirty) {
      clearTimeout(mo._t);
      mo._t = setTimeout(function () { markTargets(document); }, 80);
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  /* ---------- Header scrolled state ---------- */
  var header = document.getElementById('page-header') || document.querySelector('.orlento-header');
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      var y = window.scrollY || window.pageYOffset;
      if (header) header.classList.toggle('orlento-header--scrolled', y > 12);
      parallax(y);
      ticking = false;
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ---------- Hero parallax ---------- */
  var heroMedia = document.querySelector('.orlento-hero__media');
  function parallax(y) {
    if (!heroMedia || reduced) return;
    var rect = heroMedia.getBoundingClientRect();
    if (rect.bottom < 0) return;
    var offset = Math.min(Math.max(y * 0.08, 0), 36);
    heroMedia.style.setProperty('--orlento-parallax', offset.toFixed(1) + 'px');
    heroMedia.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
  }

  /* ---------- Magnetic primary buttons ---------- */
  if (finePointer && !reduced) {
    var MAG = '.orlento-hero__btn, .orlento-btn--accent, .orlento-cart__checkout, .orlento-pdp__atc';
    document.addEventListener('pointermove', function (ev) {
      var btn = ev.target.closest && ev.target.closest(MAG);
      if (!btn) return;
      var r = btn.getBoundingClientRect();
      var dx = (ev.clientX - (r.left + r.width / 2)) / r.width;
      var dy = (ev.clientY - (r.top + r.height / 2)) / r.height;
      btn.style.setProperty('transform', 'translate(' + (dx * 6).toFixed(1) + 'px,' + (dy * 6 - 2).toFixed(1) + 'px)', 'important');
    });
    document.addEventListener('pointerout', function (ev) {
      var btn = ev.target.closest && ev.target.closest(MAG);
      if (btn && !btn.contains(ev.relatedTarget)) btn.style.removeProperty('transform');
    });
  }

  /* ---------- Ripple ---------- */
  document.addEventListener('pointerdown', function (ev) {
    if (reduced) return;
    var btn = ev.target.closest && ev.target.closest('.orlento-btn, .orlento-cart__checkout, .orlento-pdp__atc, .orlento-sticky__atc, .orlento-product-card__btn');
    if (!btn || btn.disabled) return;
    var r = btn.getBoundingClientRect();
    var size = Math.max(r.width, r.height);
    var s = document.createElement('span');
    s.className = 'orlento-ripple';
    s.style.width = s.style.height = size + 'px';
    s.style.left = (ev.clientX - r.left - size / 2) + 'px';
    s.style.top = (ev.clientY - r.top - size / 2) + 'px';
    btn.appendChild(s);
    setTimeout(function () { if (s.parentNode) s.parentNode.removeChild(s); }, 650);
  });

  /* ---------- Cart badge bump ---------- */
  var counts = document.querySelectorAll('.cart-count, .h-count, [data-cart-count]');
  Array.prototype.forEach.call(counts, function (c) {
    var last = c.textContent.trim();
    new MutationObserver(function () {
      var now = c.textContent.trim();
      if (now === last) return;
      last = now;
      c.classList.remove('orlento-bump');
      void c.offsetWidth;
      c.classList.add('orlento-bump');
    }).observe(c, { childList: true, characterData: true, subtree: true });
  });
})();


/* Skeleton loaders tied to real events.
   - Images: every media wrapper gets .orlento-skel; .is-loaded is added when its
     image fires `load` (or is already complete), never on a timer.
   - Cart: .orlento-cart gets .is-updating while a /cart/* request is in flight.
   - Collection grid uses the theme's own body.loading flag (CSS only). */
(function () {
  var WRAPPERS = [
    '.orlento-product-card__media',
    '.orlento-collection-list__media',
    '.orlento-reviews__shot',
    '.orlento-editorial__card',
    '.bls-minicart-product__image',
    '.orlento-hero__media',
    '.orlento-video-banner__media',
    '.orlento-quick-view__media',
    '.orlento-pdp .gallery-img'
  ].join(',');

  function settle(wrap) {
    wrap.classList.add('is-loaded');
    settleAfterFade(wrap);
  }

  // Removes the pseudo-element after its fade; driven by transitionend, with a
  // guard for browsers that skip the event when the element is off-screen.
  function settleAfterFade(wrap) {
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      wrap.classList.add('is-settled');
      wrap.removeEventListener('transitionend', finish);
    }
    wrap.addEventListener('transitionend', finish);
    if (document.hidden) finish();
  }

  function track(wrap) {
    if (wrap.__orlentoSkel) return;
    wrap.__orlentoSkel = true;
    var img = wrap.querySelector('img');
    if (!img) return; // no image (placeholder SVG / video): leave the wrapper plain
    wrap.classList.add('orlento-skel');
    var loaded = function () {
      if (img.complete && img.naturalWidth > 0) settle(wrap);
    };
    if (img.complete && img.naturalWidth > 0) {
      settle(wrap);
      return;
    }
    img.addEventListener('load', loaded, { once: true });
    img.addEventListener('error', function () { settle(wrap); }, { once: true });
  }

  function scan(root) {
    var nodes = (root || document).querySelectorAll(WRAPPERS);
    Array.prototype.forEach.call(nodes, track);
  }

  scan(document);

  var mo = new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      if (muts[i].addedNodes && muts[i].addedNodes.length) {
        clearTimeout(mo._t);
        mo._t = setTimeout(function () { scan(document); }, 60);
        break;
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  /* ---- Cart requests ---- */
  if (window.fetch && !window.__orlentoFetchWrapped) {
    window.__orlentoFetchWrapped = true;
    var nativeFetch = window.fetch;
    var pending = 0;
    function cartEl() { return document.querySelector('.orlento-cart'); }
    function setUpdating(on) {
      var c = cartEl();
      if (!c) return;
      c.classList.toggle('is-updating', on);
    }
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isCart = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url) || /\/cart\?/.test(url) && init && init.method && init.method.toUpperCase() !== 'GET';
      if (!isCart) return nativeFetch.apply(this, arguments);
      pending++;
      setUpdating(true);
      var p = nativeFetch.apply(this, arguments);
      var release = function () {
        pending = Math.max(0, pending - 1);
        if (pending === 0) {
          // the theme swaps drawer markup after the promise resolves; release on the next frame
          requestAnimationFrame(function () { requestAnimationFrame(function () { setUpdating(false); }); });
        }
      };
      p.then(release, release);
      return p;
    };
  }
})();


/* WhatsApp chat widget (gty-whatsapp-chat-button, open shadow root): restyle to
   the Orlento FAB — 50px disc, WhatsApp green, soft shadow, 16px from the corner. */
(function () {
  function style() {
    var host = document.querySelector('gty-whatsapp-chat-button');
    if (!host || !host.shadowRoot) return false;
    var root = host.shadowRoot;
    if (root.querySelector('style[data-orlento-wa]')) return true;
    var s = document.createElement('style');
    s.setAttribute('data-orlento-wa', '');
    s.textContent = [
      '.gty-wacb-wrapper{right:16px!important;bottom:calc(16px + var(--orlento-wa-lift,0px))!important;transition:bottom .25s ease}',
      '.gty-wacb-button{width:50px!important;height:50px!important;background:#25d366!important;border-radius:999px!important;',
      'box-shadow:0 4px 12px rgba(0,0,0,.18),0 1px 3px rgba(0,0,0,.12)!important;',
      'transition:transform .25s cubic-bezier(.22,.61,.36,1),box-shadow .25s ease!important}',
      '.gty-wacb-button svg{width:27px!important;height:27px!important}',
      '@media (hover:hover){.gty-wacb-button:hover{transform:translateY(-2px) scale(1.04);box-shadow:0 8px 20px rgba(0,0,0,.22)!important}}',
      '.gty-wacb-button:active{transform:scale(.96)}',
      '@media (min-width:760px){.gty-wacb-wrapper{right:24px!important;bottom:calc(24px + var(--orlento-wa-lift,0px))!important}}'
    ].join('');
    root.appendChild(s);
    return true;
  }
  if (style()) return;
  var tries = 0;
  var timer = setInterval(function () {
    if (style() || ++tries > 60) clearInterval(timer);
  }, 250);
})();


/* PDP skeleton on a real signal: the theme clears .skeleton-product-detail on a
   1.5s timer. Clear it as soon as the first gallery image has actually loaded
   (or at once if it is already cached), so the page never waits on a timer and
   never reveals before the media is there. */
(function () {
  var main = document.getElementById('MainContent');
  if (!main || !main.classList.contains('skeleton-product-detail')) return;
  var gallery = document.querySelector('.orlento-pdp .product__media-gallery, .orlento-pdp .product__media-mobile, .orlento-pdp .product__media-desktop');
  if (!gallery) return;
  var done = false;
  function clear() {
    if (done) return;
    done = true;
    main.classList.remove('skeleton-product-detail');
  }
  function visible(img) {
    var r = img.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }
  var imgs = Array.prototype.filter.call(gallery.querySelectorAll('img'), visible);
  if (!imgs.length) imgs = Array.prototype.slice.call(gallery.querySelectorAll('img'), 0, 1);
  if (!imgs.length) { clear(); return; }
  var first = imgs[0];
  if (first.complete && first.naturalWidth > 0) { clear(); return; }
  first.addEventListener('load', clear, { once: true });
  first.addEventListener('error', clear, { once: true });
  // Safety net: never hold the page if the image never reports back
  window.addEventListener('load', function () { setTimeout(clear, 300); }, { once: true });
})();

  /* ------------------------------------------------------------------------
   * Boot
   * ---------------------------------------------------------------------- */
  function boot() {
    Orlento.init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    Orlento.init(event.target);
  });
})();
